<template>
  <v-dialog :value="value" :max-width="isSuccessful ? 420 : 560" persistent scrollable @input="$emit('input', $event)">
    <v-card v-if="isSuccessful" class="px-8 px-md-10 py-4">
      <v-card-title class="justify-center">發送成功</v-card-title>
      <v-card-text class="text-center care-send-success-items">
        <div v-for="item in successfulTemplates" :key="item.id" class="py-1">{{ item.name }}</div>
      </v-card-text>
      <v-card-actions>
        <v-btn color="grey-4" dark block @click="close">我知道了，關閉視窗</v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else>
      <v-card-title class="text-h6">{{ operation ? '關懷訊息發送結果' : '發送智能個案管理師訊息' }}</v-card-title>
      <v-card-text>
        <template v-if="!operation">
          <p class="black--text">{{ tag.tag_name }}：請選擇要立即發送的細項。</p>
          <p>確認後直接送出，不會建立排程。原有自動排程仍會照常發送。</p>
          <div v-if="templatesLoading" class="text-center py-4" role="status">
            <v-progress-circular indeterminate color="primary" size="24" />
            <span class="ml-2">讀取細項中…</span>
          </div>
          <v-alert v-else-if="loadError" type="error" text>{{ loadError }}</v-alert>
          <template v-else-if="templates.length">
            <v-checkbox
              :input-value="allSelected"
              :indeterminate="selectedIds.length > 0 && !allSelected"
              label="全選"
              hide-details
              class="mt-0 mb-3"
              @change="toggleAll"
            />
            <v-checkbox
              v-for="(template, index) in templates"
              :key="template.id"
              v-model="selectedIds"
              :value="template.id"
              :label="templateName(template, index)"
              hide-details
              class="mt-2"
            />
            <p v-if="!selectedIds.length" class="error--text mt-3 mb-0">最少勾選一個細項才能送出。</p>
          </template>
          <v-alert v-else type="info" text>此項目沒有可發送的細項。</v-alert>
        </template>
        <template v-else>
          <p class="black--text">{{ operation.tagName }}</p>
          <div v-if="sending" class="text-center py-4" role="status" aria-live="polite">
            <v-progress-circular indeterminate color="primary" size="24" />
            <p class="mt-3 mb-0">{{ result ? '正在確認原次發送結果…' : '正在發送，請稍候…' }}</p>
          </div>
          <template v-else-if="result">
            <v-alert :type="resultType" text role="status" aria-live="polite">{{ result.message }}</v-alert>
            <p v-if="hasCounts">
              發送成功 {{ result.sent_count }} 則、失敗 {{ result.failed_count }} 則、
              結果不明 {{ result.unknown_count }} 則、未嘗試 {{ result.not_attempted_count }} 則。
            </p>
            <v-alert v-if="unresolved" type="warning" text>
              本次可能已有訊息送出，請勿重新發送。可按「重新確認結果」查詢原次操作；若仍無法確認，請聯繫客服。
            </v-alert>
            <div v-if="result.results.length">
              <div v-for="(item, index) in result.results" :key="index" class="care-send-result py-3">
                <div class="font-weight-medium black--text">{{ resultTemplateName(item.template_id) }}</div>
                <div class="d-flex justify-space-between flex-wrap mt-1" style="gap: 8px;">
                  <span>{{ channelName(item.channel_scope) }}</span>
                  <span :class="item.status === 'accepted' ? 'success--text' : 'error--text'">{{ statusName(item.status) }}</span>
                </div>
                <div v-if="item.error_code" class="mt-1">{{ errorMessage(item.error_code) }}</div>
                <div v-if="item.log_status === 'failed' || item.log_status === 'not_written'" class="warning--text mt-1">
                  發送紀錄未完整保存，請以本次結果為準，勿因此重送。
                </div>
              </div>
            </div>
            <v-alert v-for="warning in result.warnings" :key="warning" type="warning" text class="mt-3 mb-0">
              {{ warningMessage(warning) }}
            </v-alert>
          </template>
        </template>
      </v-card-text>
      <v-card-actions class="justify-end px-6 pb-6 flex-wrap" style="gap: 8px;">
        <v-btn :disabled="sending" text @click="close">{{ operation ? '關閉' : '不發送' }}</v-btn>
        <v-btn v-if="!operation && loadError" color="primary" outlined @click="initialize">重新讀取</v-btn>
        <v-btn v-if="!operation" :disabled="!canSend" color="primary" @click="confirmSend">確認送出</v-btn>
        <v-btn v-else-if="unresolved" :loading="sending" color="primary" @click="submitOperation">重新確認結果</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue';
import { getSingleCustomizeCareTagAPI, sendCustomizeCareAPI } from '@/api';

// 保留尚未確認的操作，關閉約診小卡再開啟時仍沿用原請求；僅存於本次頁面記憶體。
const pendingOperations = new Map();

function operationKey(payload) {
  return JSON.stringify(['type', 'clinic_code', 'doctor_code', 'crm_user_id', 'line_bot_id', 'reserve_no', 'tag_id']
    .map(key => String(payload[key] == null ? '' : payload[key])));
}

function createRequestId() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function isUnresolved(result) {
  return !result || ['unknown', 'in_progress', 'conflict'].includes(result.status) ||
    result.unknown_count > 0 || result.results.some(item => item.status === 'unknown') ||
    result.warnings.includes('REQUEST_STATE_WRITE_FAILED');
}

function normalizeResult(data, previous) {
  if (!data || !data.status) {
    return {
      ...previous,
      status: 'unknown',
      message: previous ? '暫時無法取得更新結果，以下保留上次結果，請勿重新發送。' : '無法確認本次發送結果，請勿重新發送。',
      results: previous ? previous.results : [],
      warnings: previous ? previous.warnings : []
    };
  }
  return {
    ...data,
    message: data.message || data.messages || '請查看各細項發送結果。',
    results: Array.isArray(data.results) ? data.results : [],
    warnings: Array.isArray(data.warnings) ? data.warnings : []
  };
}

export default {
  name: 'SmartCareSendDialog',
  props: {
    value: Boolean,
    tag: { type: Object, required: true },
    reserveNo: { type: [String, Number], required: true },
    scope: { type: Object, required: true }
  },
  data() {
    return {
      templates: [],
      selectedIds: [],
      templatesLoading: false,
      loadError: '',
      loadSequence: 0,
      operation: null
    };
  },
  computed: {
    payloadScope() {
      return { ...this.scope, reserve_no: this.reserveNo, tag_id: this.tag.id };
    },
    allSelected() {
      return this.templates.length > 0 && this.selectedIds.length === this.templates.length;
    },
    canSend() {
      return !this.templatesLoading && !this.loadError && this.selectedIds.length > 0 && !this.operation;
    },
    sending() { return !!this.operation && this.operation.loading; },
    result() { return this.operation && this.operation.result; },
    unresolved() { return !!this.operation && isUnresolved(this.result); },
    isSuccessful() {
      return !this.sending && !!this.result && this.result.status === 'accepted' &&
        !this.unresolved && this.result.warnings.length === 0 &&
        this.result.results.length > 0 && this.result.results.every(item => item.status === 'accepted');
    },
    successfulTemplates() {
      if (!this.isSuccessful) return [];
      const seen = new Set();
      return this.result.results.reduce((items, result) => {
        const id = String(result.template_id);
        if (!seen.has(id)) {
          seen.add(id);
          items.push({ id, name: this.resultTemplateName(id) });
        }
        return items;
      }, []);
    },
    hasCounts() {
      return this.result && this.result.total_count > 0;
    },
    resultType() {
      if (this.unresolved || this.result.status === 'partial' || this.result.warnings.length) return 'warning';
      return this.result.status === 'accepted' ? 'success' : 'error';
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.initialize();
      }
    }
  },
  methods: {
    async initialize() {
      const sequence = ++this.loadSequence;
      this.operation = pendingOperations.get(operationKey(this.payloadScope)) || null;
      this.templates = [];
      this.selectedIds = [];
      this.loadError = '';
      this.templatesLoading = false;
      if (this.operation) return;
      this.templatesLoading = true;
      try {
        const response = await getSingleCustomizeCareTagAPI(this.tag.id, { ...this.scope });
        if (sequence === this.loadSequence) this.templates = response.data.data.message_templates || [];
      } catch {
        if (sequence === this.loadSequence) this.loadError = '無法讀取關懷細項，請重新讀取。';
      } finally {
        if (sequence === this.loadSequence) this.templatesLoading = false;
      }
    },
    templateName(template, index) {
      return `${this.tag.tag_name} - ${template.title || String(index + 1)}`;
    },
    toggleAll() {
      this.selectedIds = this.allSelected ? [] : this.templates.map(template => template.id);
    },
    confirmSend() {
      if (!this.canSend) return;
      const key = operationKey(this.payloadScope);
      const pending = pendingOperations.get(key);
      if (pending) {
        this.operation = pending;
        return;
      }
      let requestId;
      try {
        requestId = createRequestId();
      } catch {
        this.loadError = '無法建立安全的發送請求，請重新整理頁面後再試。';
        return;
      }
      this.operation = Vue.observable({
        key,
        payload: { ...this.payloadScope, template_ids: [...this.selectedIds], request_id: requestId },
        tagName: this.tag.tag_name,
        templates: this.templates.map((template, index) => ({ id: template.id, name: this.templateName(template, index) })),
        loading: false,
        result: null
      });
      pendingOperations.set(key, this.operation);
      return this.submitOperation();
    },
    async submitOperation() {
      const operation = this.operation;
      if (!operation || operation.loading || !isUnresolved(operation.result)) return;
      operation.loading = true;
      try {
        const response = await sendCustomizeCareAPI(operation.payload);
        operation.result = normalizeResult(response.data, operation.result);
      } catch (error) {
        operation.result = normalizeResult(error.response && error.response.data, operation.result);
      } finally {
        operation.loading = false;
        if (!isUnresolved(operation.result)) pendingOperations.delete(operation.key);
      }
    },
    resultTemplateName(id) {
      const template = this.operation.templates.find(item => String(item.id) === String(id));
      return template ? template.name : `關懷細項 ${id}`;
    },
    channelName(channel) {
      return { clinic: '診所 LINE 官方帳號', doctor: '醫師 LINE 官方帳號', default: '小幫手公用 LINE 帳號' }[channel] || 'LINE 帳號';
    },
    statusName(status) {
      return { accepted: '發送成功', failed: '發送失敗', unknown: '結果不明', not_attempted: '尚未嘗試發送' }[status] || '結果不明';
    },
    errorMessage(code) {
      return {
        LINE_REJECTED: 'LINE 未接受此訊息。',
        RESULT_UNKNOWN: '無法確認 LINE 是否已接受，請勿重送。',
        BUDGET_EXHAUSTED: '已達本次處理時間上限，此細項尚未發送。',
        NOT_ATTEMPTED: '本次處理已中止，此細項尚未發送。',
        INVALID_PAYLOAD: '訊息內容格式不正確，請檢查設定。',
        MISSING_CREDENTIAL: 'LINE 帳號設定不完整，請聯繫客服。',
        INVALID_RETRY_KEY: '發送驗證資料異常，請聯繫客服。'
      }[code] || '發送異常，請聯繫客服確認。';
    },
    warningMessage(code) {
      return {
        LOG_WRITE_FAILED: '部分發送紀錄未完整保存，請以本次結果為準，勿因此重送。',
        REQUEST_STATE_WRITE_FAILED: '本次操作結果未完整保存，可能已有訊息送出，請勿重新發送並聯繫客服。'
      }[code] || '發送紀錄或操作狀態有異常，請聯繫客服確認，勿直接重送。';
    },
    close() {
      if (this.sending) return;
      ++this.loadSequence;
      this.$emit('input', false);
    }
  }
};
</script>

<style scoped>
.care-send-success-items {
  overflow-wrap: anywhere;
}

.care-send-result {
  border-bottom: 1px solid #e5e7eb;
  overflow-wrap: anywhere;
}
</style>
